
import Navbar from '@/components/help-center/Navbar';
import Footer from '@/components/help-center/Footer';
import { HeroBackdropLayers, heroDescriptionClass } from '@/components/help-center/latest-power-shared';
import { ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from '@/lib/navigation';

import { cn } from '@/lib/utils';
import {
  isChangelogProductId,
  type ChangelogProductId,
  type ChangelogSource,
} from '@/lib/changelog/changelog-sources';
import type { ChangelogEntry, ChangelogSection } from '@/lib/changelog/changelog-types';
import { getChangelogSectionVisual } from '@/lib/changelog/changelog-section-visuals';
import { useChangelogSources } from '@/hooks/use-changelog-sources';
import PowerMentionText from '@/components/help-center/powers/PowerMentionText';

const CHANGELOG_PRODUCT_STORAGE_KEY = 'xat-changelog-product';

const PT_MONTHS: Record<string, number> = {
  janeiro: 1,
  fevereiro: 2,
  marco: 3,
  abril: 4,
  maio: 5,
  junho: 6,
  julho: 7,
  agosto: 8,
  setembro: 9,
  outubro: 10,
  novembro: 11,
  dezembro: 12,
};

function yearFromChangelogDate(date: string): number {
  const m = date.match(/(\d{4})\s*$/);
  return m ? parseInt(m[1], 10) : new Date().getFullYear();
}

/** Valor ordenável para datas no formato «9 de junho de 2026». */
function changelogDateSortValue(date: string): number {
  const normalized = date
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  const m = normalized.match(/(\d{1,2})\s+de\s+([a-z]+)\s+de\s+(\d{4})/);
  if (!m) return yearFromChangelogDate(date) * 10000;
  const day = Number(m[1]);
  const month = PT_MONTHS[m[2]] ?? 0;
  const year = Number(m[3]);
  return year * 10000 + month * 100 + day;
}

function compareSemverDesc(a: string, b: string): number {
  const pa = a.split('.').map((p) => Number.parseInt(p, 10) || 0);
  const pb = b.split('.').map((p) => Number.parseInt(p, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const da = pa[i] ?? 0;
    const db = pb[i] ?? 0;
    if (db !== da) return db - da;
  }
  return 0;
}

/** Mais recente primeiro (data, depois versão). */
function sortChangelogEntriesDesc(entries: ChangelogEntry[]): ChangelogEntry[] {
  return [...entries].sort((a, b) => {
    const byDate = changelogDateSortValue(b.date) - changelogDateSortValue(a.date);
    if (byDate !== 0) return byDate;
    return compareSemverDesc(a.version, b.version);
  });
}

function Section({ section }: { section: ChangelogSection }) {
  const [isOpen, setIsOpen] = useState(true);
  const cfg = getChangelogSectionVisual(section.type);
  const Icon = cfg.icon;

  return (
    <div className={`rounded-2xl border ${cfg.borderClass} overflow-hidden`}>
      <button
        onClick={() => setIsOpen((v) => !v)}
        className={`w-full flex items-center justify-between px-5 py-4 ${cfg.bgClass} transition-colors`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg ${cfg.badgeBgClass} flex items-center justify-center`}>
            <Icon className={`w-4 h-4 ${cfg.colorClass}`} />
          </div>
          <span className={`font-bold text-sm font-poppins ${cfg.colorClass}`}>{section.label}</span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.badgeBgClass} ${cfg.badgeTextClass}`}>
            {section.count}
          </span>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <div className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
        <div className="overflow-hidden">
          <ul className="px-5 py-4 space-y-4 bg-white">
            {section.items.map((item, i) => (
              <li key={i} className="flex gap-3">
                <span
                  className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: cfg.accent }}
                />
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    <PowerMentionText text={item.title} as="span" />
                  </p>
                  {item.details && (
                    <ul className="mt-1.5 space-y-1">
                      {item.details.map((d, j) => (
                        <li key={j} className="flex gap-2 text-sm text-gray-500">
                          <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-300 flex-shrink-0" />
                          <PowerMentionText text={d} as="span" />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function ChangelogProductSelector({
  activeProduct,
  onChange,
  sources,
  productOrder,
}: {
  activeProduct: ChangelogProductId;
  onChange: (id: ChangelogProductId) => void;
  sources: Record<ChangelogProductId, ChangelogSource>;
  productOrder: ChangelogProductId[];
}) {
  return (
    <div
      className="inline-flex rounded-full bg-white/10 p-1 ring-1 ring-white/15 backdrop-blur-sm"
      role="tablist"
      aria-label="Selecionar changelog"
    >
      {productOrder.map((id) => {
        const source = sources[id];
        const isActive = activeProduct === id;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(id)}
            className={`rounded-full px-4 py-2 font-poppins text-sm font-semibold transition-all duration-200 ${
              isActive
                ? 'bg-white text-[#030023] shadow-md'
                : 'text-blue-100/90 hover:bg-white/10 hover:text-white'
            }`}
          >
            {source.label}
          </button>
        );
      })}
    </div>
  );
}

export default function ChangelogPage() {
  const { sources, productOrder } = useChangelogSources();
  const [searchParams] = useSearchParams();
  const initialProduct = useMemo((): ChangelogProductId => {
    const fromQuery = searchParams.get('produto');
    return fromQuery && isChangelogProductId(fromQuery) ? fromQuery : 'xat';
  }, [searchParams]);

  const [activeProduct, setActiveProduct] = useState<ChangelogProductId>(initialProduct);
  const source = sources[activeProduct];
  const changelog = useMemo(
    () => sortChangelogEntriesDesc(source.entries),
    [source.entries]
  );

  const [activeVersion, setActiveVersion] = useState(changelog[0]?.version ?? '');
  const activeEntry = changelog.find((e) => e.version === activeVersion) ?? changelog[0];

  const initialOpenYear = changelog[0] ? yearFromChangelogDate(changelog[0].date) : new Date().getFullYear();
  const [openYears, setOpenYears] = useState<Record<number, boolean>>(() => ({
    [initialOpenYear]: true,
  }));

  useEffect(() => {
    try {
      const fromQuery = searchParams.get('produto');
      if (fromQuery && isChangelogProductId(fromQuery)) {
        setActiveProduct(fromQuery);
        return;
      }
      const stored = localStorage.getItem(CHANGELOG_PRODUCT_STORAGE_KEY);
      if (stored === 'xat' || stored === 'ajuda') {
        setActiveProduct(stored);
      }
    } catch {
      /* ignore */
    }
  }, [searchParams]);

  useEffect(() => {
    const entries = sortChangelogEntriesDesc(sources[activeProduct].entries);
    const preferred = entries.find((e) => e.latest)?.version ?? entries[0]?.version ?? '';
    setActiveVersion(preferred);
    const y = entries[0] ? yearFromChangelogDate(entries[0].date) : new Date().getFullYear();
    setOpenYears({ [y]: true });
  }, [activeProduct, sources]);

  const versionsByYear = useMemo((): [number, ChangelogEntry[]][] => {
    const map = new Map<number, ChangelogEntry[]>();
    for (const entry of changelog) {
      const y = yearFromChangelogDate(entry.date);
      if (!map.has(y)) map.set(y, []);
      map.get(y)!.push(entry);
    }
    return Array.from(map.entries()).sort((a, b) => b[0] - a[0]);
  }, [changelog]);

  const toggleYear = (year: number) => {
    setOpenYears((prev) => ({ ...prev, [year]: !prev[year] }));
  };

  const handleProductChange = (id: ChangelogProductId) => {
    setActiveProduct(id);
    try {
      localStorage.setItem(CHANGELOG_PRODUCT_STORAGE_KEY, id);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    const entry = changelog.find((e) => e.version === activeVersion);
    if (!entry) return;
    const y = yearFromChangelogDate(entry.date);
    setOpenYears((prev) => (prev[y] ? prev : { ...prev, [y]: true }));
  }, [activeVersion, changelog]);

  if (!activeEntry) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      <main className="flex-1">
        <section className="relative overflow-hidden bg-[#030023]">
          <HeroBackdropLayers className="absolute inset-0" />
          <div className="relative z-10 max-w-5xl mx-auto px-6 pt-40 pb-20 md:pt-48 md:pb-24">
            <div className="mb-8">
              <ChangelogProductSelector
                activeProduct={activeProduct}
                onChange={handleProductChange}
                sources={sources}
                productOrder={productOrder}
              />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-5 font-poppins">
              {source.heroTitle}
            </h1>
            <p className={cn('max-w-2xl', heroDescriptionClass)}>
              {source.heroDescription}
            </p>
          </div>
        </section>

        <section className="py-14 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto flex flex-col gap-8 lg:flex-row lg:gap-10 items-start">
            <aside className="w-full lg:w-60 flex-shrink-0 lg:sticky lg:top-28 lg:self-start">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3 px-2">
                Versões
              </p>
              <nav className="max-h-[min(70vh,520px)] overflow-y-auto pr-1 space-y-2 rounded-xl border border-gray-200/80 bg-white p-2 shadow-sm">
                {versionsByYear.map(([year, entries]) => {
                  const isOpen = openYears[year] ?? false;
                  return (
                    <div key={year} className="rounded-lg border border-gray-100 bg-gray-50/80 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleYear(year)}
                        className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left font-poppins text-sm font-semibold text-gray-800 transition-colors hover:bg-gray-100/90"
                        aria-expanded={isOpen}
                        aria-controls={`changelog-year-${activeProduct}-${year}`}
                        id={`changelog-year-trigger-${activeProduct}-${year}`}
                      >
                        <span>{year}</span>
                        <span className="flex items-center gap-1.5">
                          <span
                            className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-md bg-gray-200/95 px-1 text-[10px] font-semibold tabular-nums text-gray-700"
                            aria-label={`${entries.length} versões em ${year}`}
                          >
                            {entries.length}
                          </span>
                          <ChevronDown
                            className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                            aria-hidden
                          />
                        </span>
                      </button>
                      <div
                        id={`changelog-year-${activeProduct}-${year}`}
                        role="region"
                        aria-labelledby={`changelog-year-trigger-${activeProduct}-${year}`}
                        className={`grid transition-[grid-template-rows] duration-200 ease-out ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
                      >
                        <div className="overflow-hidden">
                          <ul className="space-y-0.5 border-t border-gray-100 bg-white px-1.5 py-1.5">
                            {entries.map((entry) => {
                              const isActive = activeVersion === entry.version;
                              return (
                                <li key={entry.version}>
                                  <button
                                    type="button"
                                    onClick={() => setActiveVersion(entry.version)}
                                    className={`w-full rounded-lg text-left transition-all duration-200 group ${
                                      isActive
                                        ? 'bg-blue-50 ring-1 ring-blue-100'
                                        : 'hover:bg-gray-50'
                                    }`}
                                  >
                                    <div className="flex items-start gap-2.5 px-2.5 py-2">
                                      <div
                                        className={`mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full transition-colors ${
                                          isActive ? 'bg-blue-500' : 'bg-gray-300 group-hover:bg-gray-400'
                                        }`}
                                      />
                                      <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-1.5">
                                          <span
                                            className={`text-sm font-semibold font-poppins ${
                                              isActive ? 'text-blue-600' : 'text-gray-700 group-hover:text-gray-900'
                                            }`}
                                          >
                                            v{entry.version}
                                          </span>
                                          {entry.latest && (
                                            <span className="rounded-full bg-blue-600 px-1.5 py-0.5 text-[9px] font-bold leading-none text-white">
                                              ATUAL
                                            </span>
                                          )}
                                        </div>
                                        <p
                                          className={`mt-0.5 text-[11px] leading-snug ${
                                            isActive ? 'text-blue-500/90' : 'text-gray-500'
                                          }`}
                                        >
                                          {entry.date}
                                        </p>
                                      </div>
                                    </div>
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </nav>
            </aside>

            <div className="flex-1 min-w-0">
              <div>
                <div className="flex items-center gap-4 mb-8">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-blue-600 text-white text-sm font-bold font-poppins shadow-md">
                      v{activeEntry.version}
                    </span>
                    {activeEntry.latest && (
                      <span className="px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
                        Versão atual
                      </span>
                    )}
                    <span className="text-sm text-gray-400 font-medium">{activeEntry.date}</span>
                  </div>
                  <div className="flex-1 h-px bg-gray-200" />
                  <div className="flex items-center gap-2">
                    {activeEntry.sections.map((s) => {
                      const cfg = getChangelogSectionVisual(s.type);
                      return (
                        <span key={s.type} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.badgeBgClass} ${cfg.badgeTextClass}`}>
                          {s.count}
                        </span>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-4">
                  {activeEntry.sections.map((section) => (
                    <Section key={section.type} section={section} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
